import React from "react"

/**
 * Helper utilities for handling number inputs that allow users to easily clear and edit values
 */

/**
 * Parses a string value to a number, allowing empty strings to remain empty
 * instead of defaulting to 0. This enables better UX for number inputs.
 */
const normalizeLocaleNumberString = (rawValue: string): string => {
  const trimmedValue = rawValue.replace(/\s/g, "").replace(/'/g, "")

  if (trimmedValue === "") {
    return ""
  }

  const usesComma = trimmedValue.includes(",")
  let normalizedValue = trimmedValue

  if (usesComma) {
    // Remove thousand separators when commas are used as decimal separator
    normalizedValue = normalizedValue.replace(/\./g, "")
  }

  // Replace the locale specific decimal separator with the JS decimal separator
  normalizedValue = normalizedValue.replace(",", ".")

  return normalizedValue
}

const germanNumberFormatter = new Intl.NumberFormat("de-DE", {
  useGrouping: false,
  maximumFractionDigits: 20,
})

export const parseNumberInput = (value: string): number | string => {
  if (value === "" || value === null || value === undefined) {
    return "" // Keep empty for better UX
  }

  const normalized = normalizeLocaleNumberString(value)
  if (normalized === "") {
    return ""
  }

  const parsed = parseFloat(normalized)
  return isNaN(parsed) ? "" : parsed
}

/**
 * Converts a number input value back to a number for storage/API calls
 * Defaults to 0 if the value is empty or invalid
 */
export const normalizeNumberInput = (value: number | string): number => {
  if (value === "" || value === null || value === undefined) {
    return 0
  }

  if (typeof value === "string") {
    const normalized = normalizeLocaleNumberString(value)
    if (normalized === "") {
      return 0
    }
    const parsed = parseFloat(normalized)
    return isNaN(parsed) ? 0 : parsed
  }

  return value
}

/**
 * Gets the display value for a number input field
 * Shows empty string if value is 0 or falsy (for better UX)
 * Otherwise shows the actual value
 */
export const getNumberInputDisplayValue = (
  value: number | string | null | undefined
): string => {
  if (value === null || value === undefined || value === "" || value === 0) {
    return ""
  }
  return germanNumberFormatter.format(
    typeof value === "number" ? value : normalizeNumberInput(value)
  )
}

export const formatNumberForLocale = (value: number | string): string => {
  if (value === null || value === undefined || value === "") {
    return ""
  }

  if (typeof value === "number") {
    return germanNumberFormatter.format(value)
  }

  const normalized = normalizeLocaleNumberString(value)
  if (normalized === "") {
    return ""
  }

  const parsed = parseFloat(normalized)
  return isNaN(parsed) ? value : germanNumberFormatter.format(parsed)
}

/**
 * Hook for managing number input state with better UX
 * Allows clearing fields and handles the conversion between display and storage values
 */
export const useNumberInput = (
  initialValue: number = 0,
  onChange?: (value: number) => void
) => {
  const [displayValue, setDisplayValue] = React.useState<string>(
    getNumberInputDisplayValue(initialValue)
  )

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setDisplayValue(newValue)

    // Only call onChange if we have a valid number or if the field is empty
    if (onChange) {
      const numericValue = normalizeNumberInput(newValue)
      onChange(numericValue)
    }
  }

  const handleBlur = () => {
    // On blur, normalize the display value
    const numericValue = normalizeNumberInput(displayValue)
    if (onChange) {
      onChange(numericValue)
    }
    // Update display to show normalized value (0 if it was empty)
    setDisplayValue(
      numericValue === 0 ? "" : germanNumberFormatter.format(numericValue)
    )
  }

  return {
    value: displayValue,
    onChange: handleChange,
    onBlur: handleBlur,
    numericValue: normalizeNumberInput(displayValue),
  }
}
